import type { RouteTypes } from '@energovizor/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import userSelector from '@/store/selectors/userSelector';
import { usePragueDate } from '@/hooks/usePragueDate';
import { apiClient } from './apiClient';
import { useGetActiveMunicipality } from './getMunicipality';

type Payload = NonNullable<RouteTypes['/obligation-alerts']['get']['response']['payload']>;
export type ObligationAlert = Payload['alerts'][number];

export const useGetObligationAlerts = ({ buildingId, enabled = true }: { buildingId?: string; enabled?: boolean } = {}) => {
  const user = useSelector(userSelector);
  const municipalityId = useGetActiveMunicipality({ enabled });
  const today = usePragueDate();
  return useQuery({
    queryKey: ['obligation-alerts', municipalityId, buildingId ?? 'all', today],
    enabled: enabled && !!municipalityId && user?.type === 'client' && !!user.verifiedEmail && !!user.verifiedByAdmin,
    refetchInterval: 60_000,
    queryFn: async () => {
      const response = await apiClient.get('/obligation-alerts', { params: { buildingId } });
      if (!response.data.payload) throw new Error('OBLIGATION_ALERTS_MISSING');
      return response.data.payload;
    },
  });
};

export const useDismissObligationAlert = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (alert: ObligationAlert) => {
      await apiClient.patch('/obligation-alerts/:alertId/dismiss', { episodeId: alert.episodeId }, { params: { alertId: alert.id } });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['obligation-alerts'] });
    },
    onError: async () => {
      toast.error('Upozornění se nepodařilo skrýt. Údaje se mohly změnit.');
      await queryClient.invalidateQueries({ queryKey: ['obligation-alerts'] });
    },
  });
};
